import { Language, formatTemplate, t } from "./i18n"
import { BsData, GPTranslation, V2GP, V2GPShard, ZpData } from "./xlsx-loader"

const hasLatin = (value: string) => /[A-Za-z]/.test(value)

export const sanitizeForWhatsApp = (value: string, lang: Language) => {
  const raw = String(value || "")
  const cleaned = raw
    .replace(/[^\p{L}\p{M}\p{N}_\s/]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()

  if (lang === "hi" || lang === "pa") {
    return cleaned.replace(/[A-Za-z]/g, " ").replace(/\s+/g, " ").trim()
  }

  return cleaned
}

export const ensureLocalizedNoLatin = (value: string, lang: Language) => {
  if (lang !== "hi" && lang !== "pa") return value
  return hasLatin(value) ? sanitizeForWhatsApp(value, lang) : value
}

export const resolveWhatsAppName = (
  level: "gp" | "bs" | "zp",
  data: {
    gpData?: GPTranslation | V2GPShard | V2GP | null
    bsData?: BsData | null
    zpData?: ZpData | null
    fallbackName?: string | null
  },
  lang: Language
): string => {
  const fallback = lang === "en" ? String(data.fallbackName || "").trim() : ""

  if (level === "gp") {
    const gp = data.gpData
    const names =
      gp && typeof gp === "object" && "sarpanch" in gp
        ? (gp as any)?.sarpanch?.names || (gp as any)?.sarpanch
        : null

    const candidate = String(names?.[lang] || "").trim()
    if (candidate) return ensureLocalizedNoLatin(candidate, lang)

    if (fallback) return ensureLocalizedNoLatin(fallback, lang)
    return t("whatsapp_name_missing", lang)
  }

  if (level === "bs") {
    const candidate = String(data.bsData?.chairperson?.names?.[lang] || "").trim()
    if (candidate) return ensureLocalizedNoLatin(candidate, lang)
    if (fallback) return ensureLocalizedNoLatin(fallback, lang)
    return t("whatsapp_name_missing", lang)
  }

  const candidate = String(data.zpData?.chairperson?.names?.[lang] || "").trim()
  if (candidate) return ensureLocalizedNoLatin(candidate, lang)
  if (fallback) return ensureLocalizedNoLatin(fallback, lang)
  return t("whatsapp_name_missing", lang)
}

export const buildWhatsAppImageRequestMessage = (params: {
  lang: Language
  name: string
  designation: string
  district: string
  block?: string
  gp?: string
  isFormerTenure?: boolean
}) => {
  const { lang, isFormerTenure } = params

  const templateKey = "whatsapp_image_request_template"
  const template = t(templateKey, lang)

  const safeName = sanitizeForWhatsApp(params.name || t("whatsapp_name_missing", lang), lang)

  const formerPrefix = isFormerTenure ? t("whatsapp_former_prefix", lang) : ""
  const safeDesignation = sanitizeForWhatsApp(`${formerPrefix}${params.designation || ""}`, lang)

  const districtRaw = params.district || ""
  const blockRaw = params.block || ""
  const gpRaw = params.gp || ""

  const districtSafe = sanitizeForWhatsApp(
    districtRaw && (lang === "en" || !hasLatin(districtRaw)) ? districtRaw : t("whatsapp_location_missing", lang),
    lang
  )
  const blockSafe = blockRaw
    ? sanitizeForWhatsApp(lang === "en" || !hasLatin(blockRaw) ? blockRaw : t("whatsapp_location_missing", lang), lang)
    : ""
  const gpSafe = gpRaw
    ? sanitizeForWhatsApp(lang === "en" || !hasLatin(gpRaw) ? gpRaw : t("whatsapp_location_missing", lang), lang)
    : ""

  return formatTemplate(template, {
    name: safeName,
    designation: safeDesignation,
    gp: gpSafe,
    block: blockSafe,
    district: districtSafe,
  })
}
